import React, { FC, useEffect, useReducer, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  createStyles,
  IconButton,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import { Close, CloudDownload } from '@material-ui/icons';
import clsx from 'clsx';
import { Skeleton } from '@material-ui/lab';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

import L from '../../utils/get-leaflet';
import MultiselectField from '../common/multiselect-field';
import { markerIcons } from '../../utils/map-marker-icons';
import FieldView from '../common/field-view';
import SelectionTool from '../common/map/controls/selection-tool';
import api from '../../api/api';
import {
  EToolActions,
  useToolActionState,
} from '../common/map/hooks/use-tool-action-state';
import { useSelection } from '../common/map/hooks/use-selection';
import { useRectDraw } from '../common/map/hooks/use-rect-draw';
import { latlngBoundsToCoords } from '../../utils/map-bbox-to-coords';

type MapProps = WithStyles<typeof styles>;

export interface OutletCategory extends EntityBase {
  types?: EntityBase[];
}


require('leaflet.markercluster');

const UniverseMap: FC<MapProps> = (props) => {
  const { classes } = props;

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);

  const [clusterGroup, setClusterGroup] = useState<L.LayerGroup | null>(null);

  const { selected, deselectAll, isOneSelected, isSelected, onSelect } =
    useSelection();

  const { action, onAction } = useToolActionState();

  const history = useHistory();

  const { rectRef: areaRef } = useRectDraw({
    map,
    canDraw: action === EToolActions.SELECT_AREA,
    onFinish: (rect) => {
      if (rect) {
        history.push(
          `/smartsale/universe/${rect.getNorthWest().lat};${
            rect.getNorthWest().lng
          };${rect.getSouthEast().lat};${rect.getSouthEast().lng}`
        );
        onAction(() => EToolActions.NOOP);
      }
    },
    removeOnFinish: true,
  });

  const [markers, setMarkers] = useState<L.Marker[]>([]);

  const [outletPoints, setOutletPoints] = useState<Array<TempPoint>>([]);

  const [queries, setQueries] = useState<EntityBase[]>([]);
  const [categories, setCategories] = useState<OutletCategory[]>([]);
  const [searchDone, setSearchDone] = useState<boolean>(false);

  const [area, setArea] = useState<L.Rectangle | null>(null);

  const params = useParams<{ bbox: string }>();

  useEffect(() => {
    if (!map) {
      return;
    }

    const bbox = (() => {
      try {
        const _coords = params.bbox
          .split(';')
          .map((coord) => parseFloat(coord));
        if (_coords.length === 4) {
          return new L.latLngBounds(
            [
              Math.min(_coords[0], _coords[2]),
              Math.min(_coords[1], _coords[3]),
            ],
            [Math.max(_coords[0], _coords[2]), Math.max(_coords[1], _coords[3])]
          );
        } else {
          console.warn('Error BBox format', _coords);
        }
      } catch (e) {
        console.warn('Error parsing coordinates of BBox', params);
      }
    })();

    if (bbox) {
      if (area) {
        area.remove();
      }
      const _rect = L.rectangle(bbox);
      map.addLayer(_rect);
      map.flyToBounds(bbox);
      setArea(_rect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.bbox, map]);

  const { rectRef: selectionRef } = useRectDraw({
    map,
    canDraw: action === EToolActions.SELECTION,
    onFinish: (selectingBounds) => {
      onSelect(
        markers.reduce<Array<number>>((acc, marker, index) => {
          if (selectingBounds.contains(marker.getLatLng())) {
            marker.setIcon(markerIcons['outlet-selected']);
            //if(goodClusterGroup.hasLayer(marker)) goodClusterGroup.
            return [...acc, index];
          }
          return acc;
        }, [])
      );
    },
    removeOnFinish: true,
  });

  type filterAction = {
    type: 'categories' | 'types';
    values: Array<string | OutletCategory>;
  };

  type filterState = Map<OutletCategory, EntityBase[]>;

  const [filter, dispatcher] = useReducer<
    (prevState: filterState, action: filterAction) => filterState
  >((state, action) => {
    if (action.type === 'categories') {
      return action.values.reduce<filterState>((acc, item) => {
        if (item['types']) {
          const category = item as OutletCategory;
          acc.set(category, []);
        }
        return acc;
      }, new Map());
    }
    if (action.type === 'types') {
      return [...state.keys()].reduce<filterState>((acc, category) => {
        acc.set(
          category,
          (action.values as EntityBase[]).filter(
            (item) => category.types?.indexOf(item) !== -1
          )
        );
        return acc;
      }, new Map());
    }
    return state;
  }, new Map());

  // useEffect(() => {
  //   dispatch(guidesActions.fetchGuides({ name: 'point_hunting_type' })).then(
  //     ({ payload, meta }) => {
  //       if (meta.requestStatus === 'fulfilled') {
  //         setCategories((payload as OutletCategory[]) || []);
  //       }
  //     }
  //   );
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const notNull = (arr: any[]) => {
    if (arr.length === 0 || (arr.length === 1 && !arr[0])) {
      return undefined;
    }
    return arr;
  };

  const onSearch = () => {
    deselectAll();
    // dispatch(
    //   mapActions.fetchTempPoints({
    //     ...(queries.length
    //       ? {
    //           query_id: queries.map((item) => '' + item.id || ''),
    //         }
    //       : {}),
    //     type_id: notNull(
    //       [...filter.keys()].flatMap((category) =>
    //         filter.get(category)?.length
    //           ? filter.get(category)?.map((item) => '' + item.id || '') || []
    //           : undefined
    //       )
    //     ),
    //     ...(area ? { bbox: latlngBoundsToCoords(area.getBounds()) } : {}),
    //   })
    // ).then((response) => {
    //   clusterGroup && clusterGroup.clearLayers();
    //   if (response.type !== '/fetchTempPoints/rejected') {
    //     setOutletPoints(
    //       (response.payload as Paginated<'data', TempPoint>).data
    //     );
    //     setSearchDone(true);
    //   }
    // });
  };

  useEffect(() => {
    if (!map) {
      return;
    }
    if (action === EToolActions.NOOP) {
      map.dragging.enable();
    } else {
      map.dragging.disable();
    }
    if (area && action === EToolActions.UNSELECT_AREA) {
      area.remove();
      setArea(null);
      onAction(() => EToolActions.NOOP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  useEffect(() => {
    if (mapContainerRef.current && !map) {
      const _map = L.map(mapContainerRef.current).setView([55.8, 37.8], 9);
      L.tileLayer('http://tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(
        _map
      );
      const _clusterGroup = L.markerClusterGroup({
        // @ts-ignore
        iconCreateFunction(cluster) {
          return L.divIcon({
            html:
              '<div class="' +
              classes.good_cluster +
              '">' +
              cluster.getChildCount() +
              '</div>',
          });
        },
        animate: true,
      });
      setClusterGroup(_clusterGroup);
      _clusterGroup.addTo(_map);
      setMap(_map);
    }
  }, [classes.bad_cluster, classes.good_cluster, map]);

  useEffect(() => {
    markers.forEach((marker, index) => {
      if (!isSelected(index)) {
        marker.setIcon(markerIcons['outlet-positive']);
      }
    });
    if (map && isOneSelected) {
      map.flyTo(outletPoints[selected[0]].latlng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneSelected, setMarkers, selected, markers, map, outletPoints]);

  useEffect(() => {
    if (!map || !clusterGroup) {
      return;
    }
    setMarkers(
      outletPoints.map((item, index) => {
        const marker = L.marker(item.latlng, {
          icon: markerIcons['outlet-positive'],
        });
        clusterGroup.addLayer(marker);
        marker.on('click', () => {
          onSelect([index]);
          marker.setIcon(markerIcons['outlet-selected']);
        });
        return marker;
      })
    );
  }, [setMarkers, onSelect, outletPoints, map, clusterGroup]);

  const onDownloadView = () => {
    if (!map) {
      return;
    }
    const bbox = map.getBounds();
    const ids = outletPoints.reduce<string[]>((acc, item, index) => {
      if (bbox.contains(markers[index].getLatLng())) {
        acc.push('' + item.id || '');
      }
      return acc;
    }, []);

    api.map.getTempOutlets({ download: '1', ids }).then(({ data }) => {
      if (data?.code === 200) {
        const linkElement = document.createElement('a');
        linkElement.href =
          'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
          data.data.content;
        linkElement.download = data.data.filename;
        document.body.appendChild(linkElement);
        linkElement.click(); // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
        document.body.removeChild(linkElement);
      }
    });
    //onDownload();
  };

  return (
    <>
      <div
        ref={mapContainerRef}
        className={clsx([
          classes.mapContainer,
          ...(action === EToolActions.SELECTION ? ['selection-action'] : []),
        ])}
      />
      {action === EToolActions.SELECTION && (
        <div className={classes.selectionPane} ref={selectionRef} />
      )}
      {action === EToolActions.SELECT_AREA && (
        <div className={classes.areaPane} ref={areaRef} />
      )}
      <div className={classes.controls}>
        <Card className={classes.filter}>
          <Box padding={2} display="flex" flexDirection="column">
            {/* <Box mb={2}>
              <MultiselectField
                id="query"
                name="query"
                label="Запрос"
                onFetchOptions={useGuide('interest_point_type')}
                onChange={(values) => setQueries(values || [])}
              />
            </Box> */}
            <Box mb={2}>
              <MultiselectField
                id="category"
                name="category"
                label="Категории ТТ"
                options={categories}
                onChange={(categories) => {
                  dispatcher({
                    type: 'categories',
                    values: categories
                      ? Array.isArray(categories)
                        ? categories
                        : [categories]
                      : [],
                  });
                }}
              />
            </Box>
            <Box mb={2}>
              <MultiselectField
                id="types"
                name="type"
                label="Типы ТТ"
                value={[...filter.values()].flatMap((value) => value)}
                options={[...filter.keys()].flatMap(
                  (value) => value.types || []
                )}
                onChange={(types) => {
                  dispatcher({ type: 'types', values: types || [] });
                }}
              />
            </Box>
            <Box display="flex">
              <Box flexGrow={1} mr={1}>
                {/* {loading && (
                  <Skeleton height={36} width="100%">
                    <Box
                      style={{ transform: 'scale(1, 1.8)' }}
                      textAlign="center"
                      visibility="visible !important"
                      height={34}
                      paddingTop={1}
                    >
                      Поиск...
                    </Box>
                  </Skeleton>
                )}
                {!loading && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={onSearch}
                  >
                    Найти
                  </Button>
                )} */}
              </Box>
              <IconButton
                onClick={onDownloadView}
                disabled={outletPoints.length === 0}
              >
                <CloudDownload />
              </IconButton>
            </Box>
            {searchDone && (
              <Typography>
                Найдено торговых точек: {outletPoints.length}{' '}
              </Typography>
            )}
          </Box>
        </Card>
        <SelectionTool active={true} action={action} onAction={onAction} />
        {isOneSelected && (
          <Card className={classes.pointInfo}>
            <CardHeader
              avatar={
                <Avatar aria-label="recipe" className={classes.avatar}>
                  {outletPoints[selected[0]].name?.slice(0, 1)}
                </Avatar>
              }
              action={
                <IconButton aria-label="settings" onClick={deselectAll}>
                  <Close />
                </IconButton>
              }
              title={outletPoints[selected[0]].name}
              subheader={outletPoints[selected[0]].address || ''}
            />
            <CardContent>
              <FieldView
                title="Телефон"
                value={outletPoints[selected[0]].phones}
              />
              <FieldView
                title="Поисковый запрос"
                value={outletPoints[selected[0]].query.name}
              />
              <FieldView
                title="Категории"
                value={outletPoints[selected[0]].categories}
              />
              <FieldView
                title="Тип"
                value={outletPoints[selected[0]].type?.name}
              />
              <FieldView
                title="Координаты"
                value={outletPoints[selected[0]].latlng.join(', ')}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    mapContainer: {
      width: '100%',
      height: '100%',
      margin: '-16px',
      position: 'absolute',
      cursor: 'grab',
      '& .leaflet-right': {
        zIndex: 1001,
      },
      '& .leaflet-div-icon': {},
      '&.selection-action': {
        cursor: 'crosshair',
      },
    },
    selectionPane: {
      position: 'absolute',
      zIndex: 999,
      border: '1px dashed #0064FF',
      backgroundColor: 'rgba(0, 100, 255, 0.2)',
    },
    areaPane: {
      position: 'absolute',
      zIndex: 999,
      border: '1px dashed #0064FF',
      backgroundColor: 'rgba(0, 255, 100, 0.2)',
      display: 'flex',
      alignItems: 'flex-end',
    },
    controls: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      zIndex: 1000,
      width: 345,
    },
    layers: {
      overflow: 'initial',
      width: 345,
      padding: '10px',
    },
    filter: {
      marginTop: '10px',
      overflow: 'initial',
      width: 345,
    },
    pointInfo: {
      marginTop: '10px',
      width: 345,
    },
    order_sum: {
      color: theme.palette.primary.main,
    },
    media: {
      height: 0,
      paddingTop: '56.25%', // 16:9
    },
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    avatar: {
      backgroundColor: theme.palette.primary.main,
    },
    good_cluster: {
      backgroundColor: '#18d300',
      height: '30px',
      width: '30px',
      textAlign: 'center',
      border: '1px solid white',
      borderRadius: '50%',
      boxShadow: '1px 1px 3px gray',
      marginTop: '-15px',
      paddingTop: '5px',
      fontWeight: 'bold',
      color: 'white',
    },
    bad_cluster: {
      backgroundColor: '#e20909',
      height: '30px',
      width: '30px',
      textAlign: 'center',
      border: '1px solid white',
      borderRadius: '50%',
      boxShadow: '1px 1px 3px gray',
      marginTop: '-15px',
      paddingTop: '5px',
      fontWeight: 'bold',
      color: 'white',
    },
  });

export default withStyles(styles)(UniverseMap);
